import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Group,
  Menu,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBackspace,
  IconChevronDown,
  IconChevronRight,
  IconCircleFilled,
  IconHeadset,
  IconPhone,
  IconPhoneOutgoing,
  IconSettings,
} from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { useSoftphone } from '@/hooks/use-softphone';
import { useNow } from '../use-now';
import { DIALPAD_KEYS, isSoftphoneConnected } from './agent-status';
import { QueueAgentsSubscription } from './graphql';
import type { WorkspaceQueue } from './types';

type StatusDefinition = { name: string; label: string; color: string };

function QueueAgentList({ queueId, statusDefinitions }: { queueId: string; statusDefinitions: StatusDefinition[] }) {
  const [{ data }] = useSubscription({ query: QueueAgentsSubscription, variables: { queueId } });
  const now = useNow(5000);

  if (!data) {
    return (
      <Text size="xs" c="dimmed" py="xs">
        Loading...
      </Text>
    );
  }

  if (data.queueMembers.length === 0) {
    return (
      <Text size="xs" c="dimmed" py="xs">
        No agents assigned
      </Text>
    );
  }

  return (
    <Stack gap={2}>
      {data.queueMembers.map((queueMember) => {
        const status = queueMember.member.agentStatuses[0] ?? null;
        const isOnline = isSoftphoneConnected(status, now);
        const definition = statusDefinitions.find((s) => s.name === status?.statusName);

        return (
          <Group key={queueMember.id} gap="xs" py={2}>
            <IconCircleFilled
              size={6}
              color={isOnline ? `var(--mantine-color-${definition?.color ?? 'gray'}-6)` : 'var(--mantine-color-dimmed)'}
            />
            <Text size="xs" truncate style={{ flex: 1 }}>
              {queueMember.member.email}
            </Text>
            <Text size="xs" c="dimmed">
              {isOnline ? (definition?.label ?? status?.statusName) : 'Offline'}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

function QueueListItem({
  queue,
  isSelected,
  statusDefinitions,
  onSelect,
}: {
  queue: WorkspaceQueue;
  isSelected: boolean;
  statusDefinitions: StatusDefinition[];
  onSelect: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div
        className="w-full rounded-md px-2 py-1.5 transition-colors"
        style={{ backgroundColor: isSelected ? 'var(--mantine-color-indigo-light)' : 'transparent' }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          onSelect();
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <ActionIcon
              variant="transparent"
              size="xs"
              color="dimmed"
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {expanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            </ActionIcon>
            <Text size="sm" fw={500} truncate>
              {queue.name}
            </Text>
            {queue.isEscalation && (
              <Badge size="xs" variant="outline" color="orange">
                esc
              </Badge>
            )}
          </Group>
          <Group gap={4} wrap="nowrap">
            {queue.waitingCount > 0 && (
              <Badge size="xs" color="orange" variant="filled">
                {queue.waitingCount}
              </Badge>
            )}
            {queue.ringingCount > 0 && (
              <Badge size="xs" color="green" variant="dot">
                ring
              </Badge>
            )}
          </Group>
        </Group>
      </div>

      <Collapse in={expanded}>
        <div className="ml-6 pl-2 border-l py-1" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
          {expanded && <QueueAgentList queueId={queue.id} statusDefinitions={statusDefinitions} />}
        </div>
      </Collapse>
    </div>
  );
}

function OutboundPanel({
  phoneNumbers,
}: {
  phoneNumbers: Array<{ phoneNumber: string; friendlyName: string | null }>;
}) {
  const { makeCall, statusLocked } = useSoftphone();
  const [expanded, setExpanded] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [callerIdNumber, setCallerIdNumber] = useState<string | null>(null);

  const callerIdOptions = phoneNumbers.map((p) => ({
    value: p.phoneNumber,
    label: p.friendlyName ? `${p.friendlyName} (${p.phoneNumber})` : p.phoneNumber,
  }));

  const dial = () => {
    const callerId = callerIdNumber ?? phoneNumbers[0]?.phoneNumber;

    if (statusLocked || !dialNumber.trim() || !callerId) return;

    makeCall(dialNumber.trim(), callerId);
    setDialNumber('');
  };

  return (
    <div className="border-t" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
      <UnstyledButton className="w-full px-3 py-2" onClick={() => setExpanded((v) => !v)}>
        <Group gap="xs">
          <IconPhoneOutgoing size={14} color="var(--mantine-color-dimmed)" />
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
            Outbound
          </Text>
          {expanded ? (
            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
          ) : (
            <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
          )}
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Stack gap="xs" px="sm" pb="sm">
          <Group gap={4}>
            <TextInput
              placeholder="Number to dial"
              value={dialNumber}
              onChange={(event) => setDialNumber(event.currentTarget.value)}
              size="xs"
              style={{ flex: 1 }}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
              onKeyDown={(event) => event.key === 'Enter' && dial()}
            />
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setDialNumber((n) => n.slice(0, -1))}
              disabled={!dialNumber}
            >
              <IconBackspace size={14} />
            </ActionIcon>
          </Group>
          <SimpleGrid cols={3} spacing={2}>
            {DIALPAD_KEYS.map((key) => (
              <Button
                key={key}
                variant="default"
                size="compact-xs"
                onClick={() => setDialNumber((n) => n + key)}
                fw={600}
                styles={{ label: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
              >
                {key}
              </Button>
            ))}
          </SimpleGrid>
          {callerIdOptions.length > 0 && (
            <Select
              data={callerIdOptions}
              value={callerIdNumber ?? callerIdOptions[0]?.value ?? null}
              onChange={setCallerIdNumber}
              size="xs"
              placeholder="Caller ID"
            />
          )}
          <Button
            leftSection={<IconPhone size={14} />}
            onClick={dial}
            disabled={!dialNumber.trim() || callerIdOptions.length === 0}
            color="green"
            size="compact-sm"
            fullWidth
          >
            Call
          </Button>
        </Stack>
      </Collapse>
    </div>
  );
}

function DevicePanel() {
  const {
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    setAudioInput,
    setAudioOutput,
  } = useSoftphone();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-t" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
      <UnstyledButton className="w-full px-3 py-2" onClick={() => setExpanded((v) => !v)}>
        <Group gap="xs">
          <IconSettings size={14} color="var(--mantine-color-dimmed)" />
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
            Devices
          </Text>
          {expanded ? (
            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
          ) : (
            <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
          )}
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Stack gap="xs" px="sm" pb="sm">
          {audioInputDevices.length > 0 && (
            <Select
              label="Microphone"
              data={audioInputDevices.map((d) => ({
                value: d.deviceId,
                label: d.label || `Mic ${d.deviceId.slice(0, 8)}`,
              }))}
              value={selectedAudioInput}
              onChange={(value) => value && setAudioInput(value)}
              size="xs"
            />
          )}
          {audioOutputDevices.length > 0 && (
            <Select
              label="Speaker"
              data={audioOutputDevices.map((d) => ({
                value: d.deviceId,
                label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`,
              }))}
              value={selectedAudioOutput}
              onChange={(value) => value && setAudioOutput(value)}
              size="xs"
            />
          )}
        </Stack>
      </Collapse>
    </div>
  );
}

export function AgentSidebar({
  email,
  extensionNumber,
  queues,
  phoneNumbers,
  outboundEnabled,
  selectedQueueId,
  onQueueSelect,
}: {
  email: string;
  extensionNumber: string | null;
  queues: WorkspaceQueue[];
  phoneNumbers: Array<{ phoneNumber: string; friendlyName: string | null }>;
  outboundEnabled: boolean;
  selectedQueueId: string | null;
  onQueueSelect: (queueId: string) => void;
}) {
  const {
    isConnected,
    isConnecting,
    statusLocked,
    connect,
    disconnect,
    setAgentStatus,
    agentStatus,
    statusDefinitions,
  } = useSoftphone();

  const currentDefinition = statusDefinitions.find((s) => s.name === agentStatus);
  const totalWaiting = queues.reduce((sum, q) => sum + q.waitingCount, 0);

  return (
    <div
      className="flex flex-col border-r shrink-0"
      style={{ width: 320, borderColor: 'var(--mantine-color-default-border)' }}
    >
      <div className="p-3 border-b" style={{ borderColor: 'var(--mantine-color-default-border)' }}>
        <Group gap="xs" mb="xs">
          <ThemeIcon size="sm" variant="transparent" color="dimmed">
            <IconHeadset size={16} />
          </ThemeIcon>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={600} truncate>
              {email}
            </Text>
            {extensionNumber && (
              <Text size="xs" c="dimmed" ff="monospace">
                ext. {extensionNumber}
              </Text>
            )}
          </div>
        </Group>

        <Menu>
          <Menu.Target>
            <Button
              variant="light"
              size="compact-sm"
              loading={isConnecting}
              leftSection={
                <IconCircleFilled
                  size={8}
                  color={
                    isConnected
                      ? `var(--mantine-color-${currentDefinition?.color ?? 'gray'}-6)`
                      : 'var(--mantine-color-dimmed)'
                  }
                />
              }
              rightSection={<IconChevronDown size={12} />}
              fullWidth
            >
              {isConnected ? (currentDefinition?.label ?? 'Online') : 'Offline'}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {statusDefinitions.map((definition) => (
              <Menu.Item
                key={definition.name}
                leftSection={<IconCircleFilled size={8} color={`var(--mantine-color-${definition.color}-6)`} />}
                disabled={statusLocked}
                onClick={() => {
                  if (definition.name === 'offline') return disconnect();
                  if (!isConnected) return void connect(definition.name);

                  void setAgentStatus(definition.name);
                }}
                fw={definition.name === agentStatus ? 600 : 400}
              >
                {definition.label}
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconCircleFilled size={8} color="var(--mantine-color-dimmed)" />}
              color="red"
              disabled={statusLocked}
              onClick={disconnect}
              fw={!isConnected ? 600 : 400}
            >
              Offline
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <ScrollArea className="flex-1" type="hover">
        <div className="p-3">
          <Group gap="xs" mb="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
              Queues
            </Text>
            {totalWaiting > 0 && (
              <Badge size="xs" color="orange" variant="filled">
                {totalWaiting}
              </Badge>
            )}
          </Group>

          {queues.length === 0 ? (
            <Text size="xs" c="dimmed" py="md" ta="center">
              Not assigned to any queues
            </Text>
          ) : (
            <Stack gap={2}>
              {queues.map((queue) => (
                <QueueListItem
                  key={queue.id}
                  queue={queue}
                  isSelected={selectedQueueId === queue.id}
                  statusDefinitions={statusDefinitions}
                  onSelect={() => onQueueSelect(queue.id)}
                />
              ))}
            </Stack>
          )}
        </div>
      </ScrollArea>

      {outboundEnabled && isConnected && !statusLocked && <OutboundPanel phoneNumbers={phoneNumbers} />}
      {isConnected && <DevicePanel />}
    </div>
  );
}
